import {
  ButtonList,
  DropdownButton,
  FlexBox,
  Icon,
  IconNames,
  Text,
} from '@dixa/lib-shared-habla';
import { DashboardType } from 'lib-dashboard-types';
import { useHasAccess } from 'lib-shared-access-control';
import { memo, MouseEvent, useCallback, useMemo } from 'react';

import { AppLink } from 'components/appLink';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useBreakpointValue } from 'utils/mediaQuery';
import { AppPath } from 'utils/url/appPaths';
import { AppUrlOptions } from 'utils/url/createAppUrl';

import { useDashboardLandingPage } from '../../utils/useDashboardLandingPage';
import { DashboardHeartbeat } from '../heartbeat/dashboardHeartbeat';

type DashboardHeaderProps = {
  dashboard: DashboardType;
};

export const DashboardHeader = memo(function DashboardHeader({
  dashboard,
}: DashboardHeaderProps): JSX.Element {
  const isSmallScreen = useBreakpointValue({ base: true, md: false });
  const hasAnalyticsAccess = useHasAccess('analytics');
  const { setLandingPage } = useDashboardLandingPage();
  const flags = useFeatureFlag(['dashboard-aggregate-favorites']);

  const handleDashboardClick = useCallback(
    (e: MouseEvent<HTMLAnchorElement>, value: DashboardType) => {
      // Prevent persisting the choice if opening in a new tab.
      if (e.ctrlKey || e.metaKey) return;

      setLandingPage({
        type: 'last_visited',
        url:
          value === 'channel'
            ? {
                path: '/dashboard/channels',
              }
            : value === 'favorite'
              ? {
                  path: '/dashboard/favorites',
                }
              : {
                  path: '/dashboard/queues',
                  params: {
                    search: {
                      type: hasAnalyticsAccess ? 'all' : 'agent',
                    },
                  },
                },
      });
    },
    [hasAnalyticsAccess, setLandingPage],
  );

  const { activeDashboard, dashboards } = useMemo(() => {
    const dashboards: Partial<
      Record<
        DashboardType,
        {
          icon: IconNames;
          label: string;
          isCurrent: boolean;
          urlOptions: AppUrlOptions<AppPath>;
        }
      >
    > = {
      queue: {
        icon: 'users',
        label: 'Queues',
        isCurrent: dashboard === 'queue',
        urlOptions: {
          path: '/dashboard/queues',
          params: {},
        },
      },
    };

    dashboards.channel = {
      icon: 'channels',
      label: 'Channels',
      isCurrent: dashboard === 'channel',
      urlOptions: {
        path: '/dashboard/channels',
      },
    };

    if (flags.dashboardAggregateFavorites) {
      dashboards.favorite = {
        icon: 'star',
        label: 'Favorites',
        isCurrent: dashboard === 'favorite',
        urlOptions: {
          path: '/dashboard/favorites',
        },
      };
    }

    return {
      activeDashboard: dashboards[dashboard]!,
      dashboards,
    };
  }, [dashboard, flags.dashboardAggregateFavorites]);

  return (
    <FlexBox gap="100" alignItems="center">
      {!isSmallScreen && (
        <>
          <Text.Heading color="tertiary" as="span">
            Real-time dashboard
          </Text.Heading>

          <Text.Heading color="tertiary" as="span" margin="0 100">
            /
          </Text.Heading>
        </>
      )}

      {Object.keys(dashboards).length === 1 ? (
        <Text.Heading as="span">{activeDashboard.label}</Text.Heading>
      ) : (
        <DropdownButton
          dropdown={
            <ButtonList>
              {Object.entries(dashboards).map(
                ([value, { icon, label, isCurrent, urlOptions }], index) => {
                  const buttonListItem = (
                    <ButtonList.Item
                      key={index}
                      icon={icon}
                      isSelected={isCurrent}
                      isDisabled={isCurrent}
                    >
                      {label}
                    </ButtonList.Item>
                  );

                  return isCurrent ? (
                    buttonListItem
                  ) : (
                    <AppLink
                      key={index}
                      {...urlOptions}
                      onClick={(e) =>
                        handleDashboardClick(e, value as DashboardType)
                      }
                    >
                      {buttonListItem}
                    </AppLink>
                  );
                },
              )}
            </ButtonList>
          }
          buttonProps={{ variant: 'tertiary' }}
          dropdownProps={{ closeOnBlur: true }}
          noStyle
          noArrow
          fixed
          data-dashboard-switcher
        >
          <FlexBox gap="200" alignItems="center">
            <Text.Heading as="span">{activeDashboard.label}</Text.Heading>
            <Icon name="angle-down" />
          </FlexBox>
        </DropdownButton>
      )}

      <DashboardHeartbeat />
    </FlexBox>
  );
});
